// The one-time "Start Client Care Checklist" Repair Tool (My Checklist spec 09/27/2026, decision 6).
// Gives the open items on buyer files and on listings at Under Contract, Pending or recently Closed
// an owner and a due date, the way new files get them since decision 4. Pure: no Firebase, no React.
// It only works where the 7 AM listing prep chaser can NEVER look, so nothing it dates or assigns
// is ever emailed. Safe to re-run: a file that needs nothing is left alone.
import { PREP_STAGES, toYMD, todayYMD, diffDays, addDays, normalizeDueRule, isDateRule, computeDeadline, coordinatorsFor, cleanDealTask } from "./listingPrep";
import { CONTRACT_STAGES, isBuyerSide, isSectionItem } from "./taskSections";
import { inScopeDeal, inScopeTask, activeOwnerOf } from "./checklistScope";

const str = (v) => String(v == null ? "" : v).trim();

// MIRRORS functions-api/listingPrepReminders.js isDealEligible (the cron's own test for a file it
// chases). Any deal this accepts is never touched by the tool.
export const cronCouldChase = (deal) => {
  if (!deal) return false;
  if (typeof deal.deletedAt === "number" && deal.deletedAt > 0) return false;
  if (deal.archived === true) return false;
  if (str(deal.dealtype).toLowerCase() === "buyer") return false;
  return PREP_STAGES.includes(deal.dealStage);
};

// A file the tool works on: live (checklistScope), not one the cron chases, and a buyer file at any
// stage or a listing at a contract stage (Cancelled and Expired are already out of scope).
export const clientCareStartApplies = (deal, today) =>
  !!deal &&
  inScopeDeal(deal, today) &&
  !cronCouldChase(deal) &&
  (isBuyerSide(deal.dealtype) || CONTRACT_STAGES.includes(str(deal.dealStage)));

const templateRuleById = (template) => {
  const map = new Map();
  Object.values(template || {}).forEach((items) => {
    (Array.isArray(items) ? items : []).forEach((it) => {
      if (it && !isSectionItem(it) && it.id !== undefined && it.dueRule) map.set(String(it.id), it.dueRule);
    });
  });
  return map;
};

// The run day is the stage's first day for "after stage" rules; a date rule counts from the deal's
// date but never lands before the run day. Rule "none" and a missing deal date stay undated.
// `held` is true when a date rule's past date was moved up to the run day, so the item must be
// marked held or the next anchor-date recompute would put the past date back.
const forwardDeadline = (rule, deal, today) => {
  if (rule.kind === "none") return { deadline: "", held: false };
  if (!isDateRule(rule)) return { deadline: addDays(today, rule.days), held: false };
  const d = computeDeadline(rule, { deal, today });
  if (!d) return { deadline: "", held: false };
  return diffDays(d, today) < 0 ? { deadline: today, held: true } : { deadline: d, held: false };
};

// One deal's tasks after the tool: `tasks` is the list as saved (the runner hands in the fresh copy
// from inside its transaction). Only open items of the file's current stage change:
//   - no active owner: the owner rule (buyer rows and a listing's unflagged and TC rows go to the
//     Client Care person; a listing's LC-flagged rows go to the LC);
//   - auto-routed to the agent's LC (routedToLC, not flagged LC): moved to the Client Care person;
//   - no due date: dated forward from the run day, never backdated.
// Completed items and other stages' items are returned untouched. Returns { tasks, assigned, dated,
// moved }; `tasks` is the same array when nothing changed.
export const clientCareStartTasks = (tasks, deal, { template = {}, users = [], today, backupId = null, byName = "" } = {}) => {
  const list = Array.isArray(tasks) ? tasks : [];
  const none = { tasks: list, assigned: 0, dated: 0, moved: 0 };
  const day = toYMD(today) || todayYMD();
  if (!clientCareStartApplies(deal, day)) return none;
  const buyer = isBuyerSide(deal.dealtype);
  const { coordinator, clientCare } = coordinatorsFor(deal.agentData, users, backupId);
  const rules = templateRuleById(template);
  let assigned = 0;
  let dated = 0;
  let moved = 0;
  const next = list.map((t) => {
    if (!inScopeTask(t, deal)) return t;
    const out = { ...t };
    let touched = false;
    const owner = activeOwnerOf(t, users);
    const handOff = (to) => {
      if (t.assignee && t.assignee.id) {
        out.handedOffFrom = t.assignee;
        out.handedOffBy = byName;
        out.handedOffAt = day;
      }
      out.assignee = to;
      touched = true;
    };
    if (!owner) {
      const to = !buyer && t.coordinatorRole ? coordinator : clientCare;
      if (to) {
        handOff(to);
        if (to === clientCare) out.routedToLC = false;
        assigned++;
      }
    } else if (
      t.routedToLC === true &&
      !t.coordinatorRole &&
      clientCare &&
      coordinator &&
      String(owner.id) === String(coordinator.id) &&
      String(owner.id) !== String(clientCare.id)
    ) {
      handOff(clientCare);
      out.routedToLC = false;
      moved++;
    }
    if (!toYMD(t.deadline)) {
      const rule = normalizeDueRule(t.dueRule || rules.get(String(t.id)));
      const { deadline, held } = forwardDeadline(rule, deal, day);
      if (deadline) {
        out.dueRule = rule;
        out.deadline = deadline;
        // recomputeAnchoredDeadlines skips items with deadlineMovedAt, so the date stays put.
        if (held) out.deadlineMovedAt = day;
        touched = true;
        dated++;
      }
    }
    return touched ? cleanDealTask(out) : t;
  });
  if (!assigned && !dated && !moved) return none;
  return { tasks: next, assigned, dated, moved };
};
